<template>
    <kgr-form-scaffold
        :pending="pending"
        :with-back-button="false"
        :save-button-disabled="saveButtonDisabled"
        :with-primary-buttons="isEditable"
        @primaryBtnClicked="save"
    >
        <kgr-loader v-if="loading" />
        <table
            v-else
            class="kgr-table kgr-table--striped kgr-table--block table-groups kgr-row"
        >
            <thead>
                <tr class="kgr-table__row">
                    <th class="kgr-table__cell--headline"></th>
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('club.exam.type') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('Bearbeiten') }}
                    </th>
                </tr>
            </thead>
            <tbody>
                <tr
                    v-for="(examType, index) in clubExamTypes"
                    :key="index"
                    class="kgr-table__row"
                >
                    <td :style="'background-color:' + examType.colour + '; width: 6px;'"></td>
                    <td
                        v-if="isMarkedEdited(examType)"
                        class="kgr-table__cell"
                    >
                        <div class="kgr-inputGroup kgr-postfix kgr-postfix--top">
                            <div class="kgr-postfix__fixed">
                                <input
                                    v-model="examType.colour"
                                    type="color"
                                    class="kgr-input"
                                    style="width: 40px;"
                                />
                            </div>
                            <kgr-form-row
                                :error="error"
                                :field-name="'clubExamTypes[' + index + '][title]'"
                                style="width: 100%;"
                            >
                                <input
                                    v-model="examType.title"
                                    type="text"
                                    maxlength="50"
                                    class="kgr-input"
                                    placeholder="Titel"
                                />
                            </kgr-form-row>
                        </div>
                        <div class="kgr-inputGroup__text kgr-inputGroup__text--error">
                            {{ showErrorMessage(index) }}
                        </div>
                    </td>
                    <td v-else class="kgr-table__cell">
                        <span :class="examType.archived ? 'kgr-color kgr-color--gray' : ''">
                            {{ examType.title }}
                        </span>
                    </td>
                    <td
                        class="kgr-table__cell"
                        style="vertical-align: top;"
                    >
                        <button
                            v-if="examType.id"
                            class="kgr-table__action"
                            :title="$t('Bearbeiten')"
                            @click="markEdited(examType)"
                        >
                            <kgr-icon-edit size="15" style="pointer-events: none" />
                        </button>
                        <button
                            v-if="isDeletable"
                            :disabled="!examType.isDeletable"
                            class="kgr-table__action"
                            :title="examType.id ? $t('Löschen') : null"
                            @click="examType.id ? remove(examType.id) : removeNewEntry(examType)"
                        >
                            <kgr-icon-delete
                                :class="{ 'kgr-icon--light noPointer': !examType.isDeletable }"
                            />
                        </button>
                        <button
                            v-if="examType.id && isEditable"
                            :disabled="examType.archived"
                            class="kgr-table__action"
                            :title="$t('Archivieren')"
                            @click="archive(examType.id, true)"
                        >
                            <kgr-icon
                                icon-class="archive"
                                :class="{ 'kgr-icon--light noPointer': examType.archived }"
                            />
                        </button>
                        <button
                            v-if="examType.id && isEditable"
                            :disabled="!examType.archived"
                            class="kgr-table__action"
                            :title="$t('Reaktivieren')"
                            @click="archive(examType.id, false)"
                        >
                            <kgr-icon
                                icon-class="unarchive"
                                :class="{ 'kgr-icon--light noPointer': !examType.archived }"
                            />
                        </button>
                    </td>
                </tr>
            </tbody>
        </table>
        <div v-if="isCreatable" class="kgr-text kgr-text--rightAligned">
            <div
                class="kgr-button kgr-button--accent"
                @click="add"
            >
                {{ $t('Prüfungsart hinzufügen') }}
            </div>
        </div>
    </kgr-form-scaffold>
</template>

<script>
import ClubExamType from '../../../js/dto/club/ClubExamType';
import ClubExamTypeRepository from '../../../js/repository/club/ClubExamTypeRepository';
import Error from '../../../js/model/Error';
import FormRow from '../../../components/common/form/FormRow.vue';
import FormScaffold from '../../../components/common/form/FormScaffold.vue';
import Icon from '../../../components/common/icon/Icon.vue';
import IconDelete from '../../../components/common/icon/IconDelete.vue';
import IconEdit from '../../../components/common/icon/IconEdit.vue';
import Loading from '../../../components/common/Loading.vue';

export default {
    components: {
        'kgr-form-row': FormRow,
        'kgr-form-scaffold': FormScaffold,
        'kgr-icon': Icon,
        'kgr-icon-delete': IconDelete,
        'kgr-icon-edit': IconEdit,
        'kgr-loader': Loading,
    },
    props: {
        clubId: {
            type: Number,
            required: true,
        },
        isCreatable: {
            type: Boolean,
            required: true,
        },
        isEditable: {
            type: Boolean,
            required: true,
        },
        isDeletable: {
            type: Boolean,
            required: true,
        },
    },
    data() {
        return {
            error: new Error(),
            pending: false,
            loading: false,
            clubExamTypes: [],
            edited: [],
        };
    },
    computed: {
        saveButtonDisabled() {
            return this.pending || this.loading;
        },
    },
    methods: {
        load(setLoading) {
            this.loading = setLoading;
            ClubExamTypeRepository.findByClubId(this.clubId)
                .then(response => this.clubExamTypes = response)
                .catch(() => this.$notify.error('error.loadingData'))
                .finally(() => this.loading = false);
        },
        save() {
            this.pending = true;
            ClubExamTypeRepository.bulkUpdate(this.clubId, this.clubExamTypes)
                .then(() => {
                    this.error = new Error();
                    this.load(false);
                    this.$notify.success('saveChanges.success');
                })
                .catch(error => {
                    this.error = error;
                    this.$notify.error('saveChanges.error');
                })
                .finally(() => this.pending = false);
        },
        remove(examTypeId) {
            showConfirm(this.$t('confirmInformal'), this.$t('Soll die Prüfungsart wirklich gelöscht werden?'), () => {
                this.pending = true;
                ClubExamTypeRepository.delete(examTypeId)
                    .then(() => this.load(false))
                    .catch(() => this.$notify.error('saveChanges.error'))
                    .finally(() => this.pending = false);
            }, 'Ok');
        },
        removeNewEntry(clubExamType) {
            this.clubExamTypes = this.clubExamTypes.filter(element => element !== clubExamType);
        },
        archive(examTypeId, archive) {
            this.pending = true;
            ClubExamTypeRepository.archive(examTypeId, archive)
                .then(() => this.load(false))
                .catch(() => this.$notify.error('saveChanges.error'))
                .finally(() => this.pending = false);
        },
        add() {
            if (this.loading || this.pending) {
                return;
            }
            let clubExamType = new ClubExamType();
            this.clubExamTypes.push(clubExamType);
            this.markEdited(clubExamType);
        },
        markEdited(examType) {
            this.edited.push(examType);
        },
        isMarkedEdited(examType) {
            return this.edited.indexOf(examType) !== -1;
        },
        showErrorMessage(index) {
            return this.error.messageForField('clubExamType[' + index + '][title]');
        },
    },
    created() {
        this.load(true);
    }
}
</script>

<style scoped lang="scss">
.noPointer {
    pointer-events: none
}
</style>
